/**
 * Session Chat Page
 * Main page component for viewing a tutoring session chat
 * Displays reading passage, message history with auto-scroll, answer input, and connection status
 *
 * MODE TRANSPARENCY AUDIT (US-005 T4): 2026-02-11
 * Verified: All mode checks are internal logic only. No mode text visible to students.
 * - Line 174: mode === 'serverless' check for popup detection (internal)
 * - Line 283: QuestionProgressIndicator shown for serverless (shows "Q1/2", not "serverless")
 * - Line 288: Polling indicator hidden for serverless (absence not leaking mode)
 * - Line 293: Complete button hidden for serverless (auto-complete behavior, not mode indicator)
 * PassagePanel, ConversationPanel, MessageBubble render identically for both modes.
 *
 * US-017 (resume affordance): isTimedOut is now always false (useSessionTimeout neutralised).
 * isSessionCompleted derives solely from status === 'completed' | 'cancelled' (AC-2).
 * Added passage resume heading (truncated) for returning students (AC-1, AC-3).
 *
 * US-016 (Report Issue): Added gated "Report a problem" affordance that appears ONLY when
 * isSystemError(currentErrorCode) is true. Wires useCancelSession + ReportIssueDialog.
 * CCM-03: no coin language visible to student.
 * TO-LL-013: navigation to /sessions lives in useCancelSession.onSuccess only.
 *
 * US-001 (Learning Tip Popup): Added serverless-only tip-enqueue effect that reads
 * showTipPopup/learningTip from question_complete messages and drives LearningTipPopup.
 * Converted popupQueue/currentPopup to a tagged union (TipPopupItem | CelebrationPopupItem)
 * so tip and celebration share the existing single-popup-at-a-time queue (DD-005).
 * shownTipForQuestionRef deduplicates by question number (AC-4).
 * useTutorTipsConfig provides countdown from GET /system-config/tutor-tips with fallback (API-001).
 *
 * US-004 (Tip Popup Sequencing): Fixed cross-source ordering race so tip always precedes
 * celebration on struggled questions (AC-1). Root cause: celebration enqueued from the
 * immediate POST response; tip enqueued from the 500ms-delayed messages refetch.
 * Fix: pendingCelebrationRef stashes the celebration until the tip enqueues it flushed
 * behind the tip in one deterministic batch. First-attempt-correct (AC-4) path is
 * byte-identical to pre-US-004 behaviour. 1500ms fallback timer ensures celebration is
 * never permanently lost if tip does not arrive (NFR1). TO-LL-037 enforced throughout.
 */

import { useParams, useNavigate } from 'react-router-dom';
import { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { useSessionMessages } from '@/hooks/useSessionMessages';
import { useSubmitAnswer } from '@/hooks/useSubmitAnswer';
import { useCompleteSession } from '@/hooks/useCompleteSession';
// BUG-20260209-105903: Import useQueryClient for profile cache invalidation - 2026-02-09
import { useQueryClient } from '@tanstack/react-query';
import { PassagePanel } from '@/components/PassagePanel';
import { ConversationPanel } from '@/components/ConversationPanel';
import { LoadingState } from '@/components/LoadingState';
import { ErrorState } from '@/components/ErrorState';
import { ChevronLeft, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useViewportHeight } from '@/hooks/useViewportHeight';
import { PebblesDisplay } from '@/components/PebblesDisplay';
import { usePebbles } from '@/hooks/usePebbles';
import { CelebrationPopup } from '@/components/CelebrationPopup';
import { DemoLimitPopup } from '@/components/DemoLimitPopup';
import type { DemoLimitReason } from '@/components/DemoLimitPopup';
// US-001: Learning tip popup component and config hook
import { LearningTipPopup } from '@/components/LearningTipPopup';
import { useTutorTipsConfig } from '@/hooks/useTutorTipsConfig';
import { SessionCompletionSummary } from '@/components/SessionCompletionSummary';
import { deriveSessionCompletionMetrics } from '@/lib/sessionCompletionMetrics';
import type { PebblesAward } from '@/types/pebbles';
import type { TutorTurnResponse, SessionSummary } from '@/types/session';
import { StudentProfileHeader } from '@/components/StudentProfileHeader';
import { QuestionProgressIndicator } from '@/components/QuestionProgressIndicator';
import { AppLogo } from '@/components/AppLogo';
import { ROUTES } from '@/constants/routes';
import { useSessionTimeout } from '@/hooks/useSessionTimeout';
import { truncateText } from '@/lib/formatters';
import { useAuth } from '@/hooks/useAuth';
import { useDemoLimits } from '@/hooks/useDemoLimits';
import { useDemoSessionTracker } from '@/hooks/useDemoSessionTracker';
// US-016: Report Issue imports
import { isSystemError } from '@/lib/systemErrorCodes';
import { useCancelSession } from '@/hooks/useCancelSession';
import { ReportIssueDialog } from '@/components/ReportIssueDialog';

/**
 * SessionChatPage - Displays session chat with real-time message updates
 */
export function SessionChatPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const messageContainerRef = useRef<HTMLDivElement>(null);
  // BUG-20260209-105903: Query client for cache invalidation on profile upload - 2026-02-09
  const queryClient = useQueryClient();

  // Monitor viewport height for mobile keyboard handling
  useViewportHeight();

  // Validate sessionId exists
  if (!sessionId) {
    return (
      <ErrorState
        title="Invalid Session"
        message="Session ID is missing. Please select a session from the list."
        onRetry={() => navigate('/sessions')}
      />
    );
  }

  // Fetch session messages with adaptive polling
  // US-019 T4: add starRating to destructure — server-derived passthrough (DD-1, TS-014).
  // Frontend never recomputes (TO-LL-024, TO-LL-044). null for loading/cancelled/non-rated.
  // BUG-20260710-162439 (RC-002): destructure the persisted sessionSummary
  // (renamed to persistedSessionSummary to distinguish it from the ephemeral
  // in-memory serverlessSessionSummary state declared below).
  const {
    messages,
    // first-run-guide (T9): who this session is about. See identity block below.
    student,
    passage,
    passageTitle,
    status,
    starRating,
    sessionSummary: persistedSessionSummary,
    persistedQuestionProgress,
    isLoading,
    error,
    isPolling,
    totalQuestions,
    mode,
    // BUG-20260731-163953 (RC-004/RC-005, T-18): passthrough, mirroring the
    // persistedQuestionProgress destructure directly above.
    segmentationDegraded,
  } = useSessionMessages(sessionId);

  // Answer submission hook (pass mode for dual-mode routing)
  const {
    submitAnswer,
    isSubmitting,
    error: submitError,
    serverlessResponse,
  } = useSubmitAnswer(sessionId, mode);

  // Session completion hook
  const { complete: completeSession, isCompleting } = useCompleteSession(sessionId);

  // US-016: Cancel session hook (Report Issue flow)
  // TO-LL-013: navigate('/sessions') lives in useCancelSession.onSuccess — NOT here.
  // TO-LL-036: cancelError is never swallowed; surfaced to ReportIssueDialog for retry.
  const {
    cancel: cancelSession,
    isCancelling,
    error: cancelError,
    reset: resetCancel,
  } = useCancelSession(sessionId);

  // US-016: Local state for Report Issue dialog visibility
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);

  // Get initial Pebbles count from user profile (fallback)
  const { totalPebbles: initialPebbles, profile } = usePebbles();

  // US-001: Fetch tip popup countdown from /system-config/tutor-tips (API-001).
  // Falls back to DEFAULT_TIP_COUNTDOWN_SECONDS=4 on error (never blocks session).
  const { countdownSeconds } = useTutorTipsConfig();

  // US-005: Demo limits for free-tier users
  const { isFreeUser, maxAttemptsPerQuestion, maxQuestionsPerSession, maxSessions } =
    useDemoLimits();

  // US-005: Get userId for auto-complete API calls
  const { user } = useAuth();

  // ── first-run-guide (T9): whose session is this, and who is looking at it? ──────────────
  //
  // A parent can now open her child's session while staying signed in as herself, so for the
  // first time the VIEWER and the STUDENT can be different people. This screen previously had
  // no way to tell them apart — the session payload carried no student identity at all, which
  // is exactly why it renders the viewer.
  //
  // ⚠️ Decided on userId, never on display name. Names are renameable and not unique, and this
  // decision gates a control that writes to the viewer's own profile.
  //
  // Falls back to "viewer is the student" when the payload predates this field, which preserves
  // the old behaviour exactly for every existing session.
  const isDrivingForChild = !!student?.userId && !!user?.userId && student.userId !== user.userId;

  // What the screen SHOWS: the student. What `usePebbles` returns is the VIEWER, which is only
  // the right answer when they are the same person.
  const displayName = isDrivingForChild
    ? (student?.displayName ?? '')
    : (profile?.displayName ?? '');
  const displayAvatarUrl = isDrivingForChild ? (student?.avatarUrl ?? undefined) : profile?.profileImageUrl;

  // ⚠️ First paint only. Once a turn awards pebbles, displayPebbles (below) takes over and stays
  // authoritative — never wire a live counter to the payload value.
  const initialPebblesForDisplay = isDrivingForChild ? (student?.totalPebbles ?? 0) : initialPebbles;

  // Whether this viewer may answer, decided server-side by the same function the answer endpoint
  // calls. Checked HERE, on arrival — not after she has typed. Walking into a session is
  // client-side navigation and makes no request, so without this a parent would read the passage,
  // write an answer, and only then be refused. Absent on older payloads: assume allowed, which is
  // the pre-existing behaviour (the endpoint still refuses her, exactly as it does today).
  const viewerMayAnswer = student?.canAnswer ?? true;

  // US-005: Track attempt counts, completed questions, and auto-complete state
  // BUG-20260222-104604: Pass serverlessQuestionJustCompleted so the hook skips auto-complete
  // when the serverless backend already completed the question (pebblesAwarded received). - 2026-02-22
  // BUG-20260227-122919: Also include maxAttemptsReached in serverlessQuestionJustCompleted
  // so the hook correctly skips auto-complete when the backend force-completed the question - 2026-02-27.
  const {
    isAttemptLimitReached,
    isQuestionLimitReached,
    isAutoCompleting: _isAutoCompleting,
    demoPopupReason,
    dismissDemoPopup,
  } = useDemoSessionTracker(
    sessionId,
    messages,
    isFreeUser,
    maxAttemptsPerQuestion,
    maxQuestionsPerSession,
    user?.userId,
    mode,
    !!(serverlessResponse?.pebblesAwarded || serverlessResponse?.maxAttemptsReached)
  );

  // US-006: Local state to handle backend-reported session_limit (supplements useDemoSessionTracker)
  const [backendDemoPopupReason, setBackendDemoPopupReason] = useState<DemoLimitReason | null>(
    null
  );

  // IMPL stage-escalation: persistence Pebbles awarded on an attempt-cap (Exit B)
  // completion, passed into the encouraging attempt_limit popup copy.
  const [attemptLimitPebbles, setAttemptLimitPebbles] = useState<number | null>(null);

  // US-006: Detect backend 403 demo limit errors (AC-006.8)
  // Covers DEMO_SESSION_LIMIT which useDemoSessionTracker does not handle (session-level, not message-derived)
  useEffect(() => {
    if (!submitError) return;
    const code = submitError.code;
    if (code === 'DEMO_ATTEMPT_LIMIT') {
      setBackendDemoPopupReason('attempt_limit');
    } else if (code === 'DEMO_QUESTION_LIMIT') {
      setBackendDemoPopupReason('question_limit');
    } else if (code === 'DEMO_SESSION_LIMIT') {
      setBackendDemoPopupReason('session_limit');
    }
  }, [submitError]);

  // Handle dismissal of any demo limit popup (tracker or backend-reported)
  // BUG-20260728 (restate-close UX): while a restate close is being acknowledged, hold
  // back the freshly-revealed next-question intro so the child reads the closing message
  // (and the gentle popup) first; the next question is revealed on dismiss below.
  const [holdRestateNextQuestion, setHoldRestateNextQuestion] = useState(false);

  const handleDemoLimitDismiss = useCallback(() => {
    if (backendDemoPopupReason !== null) {
      setBackendDemoPopupReason(null);
    } else {
      dismissDemoPopup();
    }
    // IMPL stage-escalation: clear the stashed persistence award so a later
    // popup never shows a stale amount.
    setAttemptLimitPebbles(null);
    // BUG-20260728 (restate-close UX): the child has acknowledged the close — reveal the
    // held next-question intro now.
    setHoldRestateNextQuestion(false);
  }, [backendDemoPopupReason, dismissDemoPopup]);

  // BUG-20260728 (restate-close UX): the messages actually RENDERED. While a restate
  // close is being acknowledged (holdRestateNextQuestion), drop the trailing next-question
  // intro (decisionType 'question') so it neither shows nor becomes answerable until the
  // child dismisses the gentle popup. Rendering-only — the effects above still read the
  // full `messages`. The backend appends the next-question intro as the last message on
  // the close turn, so it is always the trailing entry when holding.
  const displayMessages = useMemo(() => {
    // length <= 1 guard: a next-question intro to hold ALWAYS follows a completed
    // question (a full prior conversation), so never drop a lone/only message.
    if (!holdRestateNextQuestion || !messages || messages.length <= 1) return messages;
    const last = messages[messages.length - 1];
    return last?.decisionType === 'question' ? messages.slice(0, -1) : messages;
  }, [messages, holdRestateNextQuestion]);

  // Track latest pebbles total from serverless responses (for live display updates)
  const [serverlessPebblesTotal, setServerlessPebblesTotal] = useState<number | null>(null);

  // Track question progress from serverless responses (the LIVE value)
  const [serverlessQuestionProgress, setQuestionProgress] =
    useState<TutorTurnResponse['questionProgress']>();

  // Track serverless session summary when session completes
  const [serverlessSessionSummary, setServerlessSessionSummary] = useState<SessionSummary | null>(
    null
  );

  // BUG-20260710-162439 (RC-002): prefer the LIVE in-memory summary (set only right
  // after a completing POST /respond, above) and fall back to the PERSISTED summary
  // (from GET /messages, present on reload) only when the in-memory one is null.
  // Live-completion render is byte-for-byte unchanged — serverlessSessionSummary
  // still wins whenever it is set; only the reload path (serverlessSessionSummary
  // === null) now resolves to the persisted value instead of null.
  const effectiveSessionSummary = serverlessSessionSummary ?? persistedSessionSummary ?? null;

  // GAP-C (C-9): the same live-then-persisted resolution, for the same reason, one
  // line below the precedent that established it.
  //
  // The live path is BYTE-FOR-BYTE UNCHANGED: the in-memory value still wins
  // whenever it is set, so a normal turn behaves exactly as it did before. Only the
  // `undefined` case — which is EVERY fresh page load — now resolves to the
  // persisted value instead of to nothing.
  //
  // That case is not cosmetic here. `phase` rides on this object, and an absent
  // phase reads as `scaffold`, which leaves the structured widget mounted. Without
  // this line, a child who reloads mid-restate is asked to explain the answer in
  // their own words while facing a table grid or radio buttons.
  const questionProgress = serverlessQuestionProgress ?? persistedQuestionProgress ?? undefined;

  // Extract latest totalPebbles from question_complete messages
  // This provides live updates during the session without extra API calls
  const latestPebbles = useMemo(() => {
    if (!messages || messages.length === 0) return null;

    // Scan messages in reverse to find the most recent question_complete with Pebbles data
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.decisionType === 'question_complete' && msg.updatedTotalPebbles !== undefined) {
        return msg.updatedTotalPebbles;
      }
    }
    return null;
  }, [messages]);

  // Use latest from serverless response, then messages, then fall back to initial from profile
  const displayPebbles = serverlessPebblesTotal ?? latestPebbles ?? initialPebblesForDisplay;

  // Calculate last activity timestamp from latest message (for timeout check)
  // US-017: kept for hook signature compatibility; hook no longer uses it to
  // fire a completion mutation (AC-2).
  const lastActivityTimestamp = useMemo(() => {
    if (!messages || messages.length === 0) return undefined;
    // Use the timestamp of the most recent message
    return messages[messages.length - 1].timestamp;
  }, [messages]);

  // SR3 US-002: Determine if it is the student's turn (last message is from tutor)
  const isStudentTurn =
    messages != null && messages.length > 0 && messages[messages.length - 1]?.role === 'tutor';

  // US-017 AC-2: useSessionTimeout is neutralised — isTimedOut is always false.
  // The hook is still called to keep the call-site stable; callers that pass
  // isTimedOut to ConversationPanel continue to work (it is always false now).
  const { isTimedOut, timeoutMessage } = useSessionTimeout(
    sessionId,
    mode,
    lastActivityTimestamp,
    status,
    isStudentTurn
  );

  // TS-007 (T6, AC-TS007.1/AC-TS007.2): local signal for the serverless-only
  // 'ended by parent' terminal state. MCP sessions get the authoritative status
  // from the next poll (T5 widened guards — useSessionMessages stops polling and
  // isSessionActive flips false the moment ended_by_parent is observed). Serverless
  // sessions never poll (useSessionMessages.ts refetchInterval returns false for
  // mode==='serverless'), so this page must detect a parent-driven end itself:
  // either a direct sessionStatus==='ended_by_parent' signal on a (hypothetical)
  // successful response, or the next submit being rejected because the session is
  // no longer active. Coordination note (TS-007 decision log): the backend's
  // tutor_turn handler (TS-003) rejects a submit against BOTH an already-'completed'
  // AND an 'ended_by_parent' session with the SAME 422 'SESSION_COMPLETED' code —
  // this is an accepted, documented assumption (a same-moment auto-complete race
  // showing the parent-ended message is a rare, accepted edge case).
  const [serverEndedByParent, setServerEndedByParent] = useState(false);

  useEffect(() => {
    if (serverlessResponse?.sessionStatus === 'ended_by_parent') {
      setServerEndedByParent(true);
    }
  }, [serverlessResponse]);

  useEffect(() => {
    if (submitError?.code === 'SESSION_COMPLETED') {
      setServerEndedByParent(true);
    }
  }, [submitError]);

  // TS-007 (T6, AC-TS007.1): true once EITHER the authoritative status says
  // 'ended_by_parent' (MCP next poll) OR the local serverless signal above fired.
  // Drives the distinct terminal message/header branch below, and excludes the
  // (nonexistent) completion summary for an ended session.
  const isEndedByParent = status === 'ended_by_parent' || serverEndedByParent;

  // US-017 AC-2: isSessionCompleted is now solely driven by status.
  // TS-018 CCM-01: 'cancelled' is treated as terminal alongside 'completed'.
  // TS-007 (T6, AC-TS007.1): 'ended_by_parent' (via isEndedByParent above) is ALSO
  // terminal — ConversationPanel locks input the same way it does for completed/cancelled.
  // isTimedOut is always false after US-017 neutralisation, so the '|| isTimedOut'
  // branch is a no-op — kept for backward-compat with other code that reads this var.
  const isSessionCompleted =
    status === 'completed' || status === 'cancelled' || isTimedOut || isEndedByParent;

  // US-016 AC-1/AC-2/AC-3: Derive the most relevant current error code from all
  // known error sources on this page. Prefer the most recent / specific error.
  // Order: submitError (answer submission) > page messages error > cancelError (own).
  // For serverless is_fallback-after-retry the code accompanies a SERVER_ERROR or
  // PROVIDER_TIMEOUT in submitError — the same derivation covers that case.
  const currentErrorCode: string | undefined =
    submitError?.code ??
    (error as { code?: string } | null)?.code ??
    cancelError?.code ??
    undefined;

  // US-016 AC-1/AC-2: Only show the Report affordance for genuine system errors.
  // isSystemError is an O(1) Set lookup (never shows for demo/validation/business errors).
  // Hidden once the session is terminal (completed/cancelled) — no point cancelling then.
  const showReportIssue = !isSessionCompleted && isSystemError(currentErrorCode);

  // US-017 AC-1/AC-3: Derive a short passage heading for the resume affordance, so a
  // returning student immediately recognises what they are resuming.
  // Prefer the passage TITLE (from contentSnapshot); fall back to a truncated first line
  // for legacy/MCP sessions that have no stored title. Memoised to avoid recompute.
  const resumePassageHeading = useMemo(() => {
    const title = passageTitle?.trim();
    if (title) return title;
    return passage ? truncateText(passage, 60) : null;
  }, [passageTitle, passage]);

  // US-003: Gate demo popup on session NOT being completed.
  // When viewing a completed session, effectiveDemoPopupReason is forced to null so
  // DemoLimitPopup is never rendered over the session history.
  // BUG-20260227-181201: Allow backendDemoPopupReason through even when session is
  // completed, because the last question's max-attempts-reached and session completion
  // arrive in the same response. Only suppress tracker-derived reasons on review.
  const effectiveDemoPopupReason: DemoLimitReason | null =
    isSessionCompleted && !backendDemoPopupReason
      ? null
      : (backendDemoPopupReason ?? demoPopupReason);

  // Derive completion metrics from messages and serverless summary (memoized)
  // US-017: isTimedOut is always false so the timeout-override branch is a no-op.
  // timeoutMessage kept as dependency for linter cleanliness if timeoutMessage
  // changes; the branch is never entered.
  const completionMetrics = useMemo(() => {
    // BUG-20260710-162439 (RC-002): use effectiveSessionSummary (live-first,
    // persisted-fallback) instead of serverlessSessionSummary directly, so a
    // reload of a completed serverless session gets the correct persisted
    // pebbles/streak data. deriveSessionCompletionMetrics itself is UNCHANGED —
    // it already prefers whatever summary it is supplied.
    const metrics = deriveSessionCompletionMetrics(messages, effectiveSessionSummary);

    // Override improvement tip with timeout message if timed out
    // US-017: isTimedOut is always false — this branch is preserved for
    // backwards-compat only and will never execute.
    if (isTimedOut) {
      return {
        ...metrics,
        improvementTip: timeoutMessage,
      };
    }

    return metrics;
  }, [messages, effectiveSessionSummary, isTimedOut, timeoutMessage]);

  // --- CelebrationPopup / LearningTipPopup integration (TS-010 + US-001 + US-002 serverless) ---

  // US-001 / DD-005: Tagged-union popup queue so tip and celebration share one queue,
  // maintaining the existing "only one popup at a time" invariant.
  type TipPopupItem = { kind: 'tip'; tipText: string };
  type CelebrationPopupItem = { kind: 'celebration'; award: PebblesAward };
  type QueuedPopup = TipPopupItem | CelebrationPopupItem;

  // Track which question_complete message IDs have already triggered a popup
  const processedPopupIdsRef = useRef<Set<string>>(new Set());

  // Track if this is the initial load to skip existing messages
  const isInitialLoadRef = useRef(true);

  // US-001 / AC-4: Dedup tips by question number — a tip shows at most once per question,
  // even across repeated polls. Mirrors processedPopupIdsRef but keyed on question number.
  const shownTipForQuestionRef = useRef<Set<number>>(new Set());

  // US-004 / AC-1 / TO-LL-037: Stash a struggled completion's celebration until its tip
  // enqueues, so the tip always precedes the celebration in the popup queue.
  // Root cause fixed: celebration comes from the immediate POST response (serverlessResponse);
  // the tip comes from the 500ms-delayed messages refetch — this ref bridges the gap.
  // Enforces popup priority discipline: tip before celebration on struggled questions.
  const pendingCelebrationRef = useRef<PebblesAward | null>(null);
  const pendingCelebrationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // BUG-20260728 (restate-close UX): the attempt_limit popup used to fire synchronously
  // from the immediate POST response, BEFORE the closing tutor message (which arrives on
  // the ~500ms messages refetch) had rendered — so the child never got to read the
  // closing message. This timer defers the popup so that message renders and gets a
  // short reading beat first, mirroring the pendingCelebration delay above.
  const pendingAttemptLimitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // US-001: Initial-load guard for the tip-enqueue effect (mirrors isInitialLoadRef above).
  const isInitialTipLoadRef = useRef(true);

  // Queue of popup items waiting to be shown (celebration or tip)
  const [popupQueue, setPopupQueue] = useState<QueuedPopup[]>([]);

  // The currently displayed popup item (null = no popup visible)
  const [currentPopup, setCurrentPopup] = useState<QueuedPopup | null>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messageContainerRef.current && messages && messages.length > 0) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Handle serverless response pebbles awards, progress, and session summary (US-002, US-003)
  useEffect(() => {
    if (!serverlessResponse) return;

    // Update question progress
    setQuestionProgress(serverlessResponse.questionProgress);

    // Capture session summary when session completes
    if (serverlessResponse.sessionSummary) {
      setServerlessSessionSummary(serverlessResponse.sessionSummary);
    }

    // IMPL stage-escalation (supersedes the BUG-20260303-160506 ordering): check
    // maxAttemptsReached FIRST. Exit B (attempts exhausted) now carries BOTH the flag
    // AND a persistence-pebbles award — the encouraging attempt-cap popup must win over
    // the celebration. Exit A (correct/restate accepted at the cap) has pebblesAwarded
    // WITHOUT the flag and still celebrates normally.
    if (serverlessResponse.maxAttemptsReached) {
      // Persistence award: update the live total and stash the amount for the popup copy.
      const persistenceAward = serverlessResponse.pebblesAwarded;
      if (persistenceAward?.updatedTotalPebbles !== undefined) {
        setServerlessPebblesTotal(persistenceAward.updatedTotalPebbles);
      }
      setAttemptLimitPebbles(persistenceAward?.totalAwarded ?? null);
      // BUG-20260728 (restate-close UX): hold back the freshly-revealed next-question
      // intro until the child dismisses the gentle popup (revealed in handleDemoLimitDismiss),
      // so the closing message is read first and the child cannot jump ahead to the next
      // question during the reading beat.
      setHoldRestateNextQuestion(true);
      // BUG-20260728 (restate-close UX): defer the gentle popup so the closing tutor
      // message renders (it arrives on the ~500ms messages refetch) and can be read
      // first, instead of the popup pre-empting it. The pebble total updated immediately
      // above; only the popup is deferred. 1800ms ≈ the refetch (~500ms) + a short
      // reading beat. Clear any prior pending timer so rapid completions do not stack.
      if (pendingAttemptLimitTimerRef.current) clearTimeout(pendingAttemptLimitTimerRef.current);
      pendingAttemptLimitTimerRef.current = setTimeout(() => {
        pendingAttemptLimitTimerRef.current = null;
        setBackendDemoPopupReason('attempt_limit');
      }, 1800);
    } else if (serverlessResponse.pebblesAwarded) {
      const award = serverlessResponse.pebblesAwarded;
      setServerlessPebblesTotal(award.updatedTotalPebbles);

      if (award.bonusAwarded === 0) {
        // US-004 / AC-1 / TO-LL-037: Struggled-but-correct (q_attempts>1): a tip WILL
        // arrive on the messages refetch (500ms delay). Stash the celebration so it does
        // NOT promote ahead of the (delayed) tip — eliminates the cross-source ordering race.
        // Implements popup priority discipline: tip before celebration on struggled questions.
        pendingCelebrationRef.current = award;
        // Graceful degrade (NFR1): if no tip resolves (empty library / showTipPopup=false),
        // flush after 1500ms so the celebration is never permanently lost.
        if (pendingCelebrationTimerRef.current) clearTimeout(pendingCelebrationTimerRef.current);
        pendingCelebrationTimerRef.current = setTimeout(() => {
          const held = pendingCelebrationRef.current;
          if (!held) return;
          pendingCelebrationRef.current = null;
          pendingCelebrationTimerRef.current = null;
          setPopupQueue((prev) => [...prev, { kind: 'celebration', award: held }]);
        }, 1500);
      } else {
        // First-attempt-correct (bonus>0): no tip is coming — enqueue immediately.
        // US-004 / AC-4: This branch is UNCHANGED — byte-identical to pre-US-004 behaviour.
        setPopupQueue((prev) => [...prev, { kind: 'celebration', award }]);
      }
    }
  }, [serverlessResponse]);

  // Detect new question_complete messages (MCP mode) and enqueue popup data
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    if (mode === 'serverless') return; // Skip for serverless (handled by serverlessResponse effect)

    // On initial load, mark all existing question_complete as already processed
    // (we only want to show popups for NEW completions detected via subsequent polls)
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      for (const msg of messages) {
        if (msg.decisionType === 'question_complete' && msg.totalAwarded !== undefined) {
          processedPopupIdsRef.current.add(msg.id);
        }
      }
      return; // Skip enqueuing on initial load
    }

    const newAwards: PebblesAward[] = [];

    for (const msg of messages) {
      // Only process question_complete messages with Pebbles data
      if (
        msg.decisionType === 'question_complete' &&
        msg.totalAwarded !== undefined &&
        msg.bonusAwarded !== undefined &&
        !processedPopupIdsRef.current.has(msg.id)
      ) {
        // Mark as processed immediately to prevent duplicate triggers
        processedPopupIdsRef.current.add(msg.id);

        newAwards.push({
          basePebbles: msg.basePebbles ?? 2,
          bonusAwarded: msg.bonusAwarded,
          totalAwarded: msg.totalAwarded,
          updatedTotalPebbles: msg.updatedTotalPebbles ?? 0,
        });
      }
    }

    if (newAwards.length > 0) {
      // US-001/DD-005: wrap each award as a tagged-union celebration item.
      setPopupQueue((prev) => [
        ...prev,
        ...newAwards.map((award) => ({ kind: 'celebration' as const, award })),
      ]);
    }
  }, [messages, mode]);

  // US-001 / US-004 (CCM-001): serverless-only tip detection.
  // The MCP effect above early-returns for serverless, so tips need their own reader.
  // Mirror-image of the MCP effect: only runs when mode === 'serverless'.
  // AC-4: shownTipForQuestionRef deduplicates by question number across polls.
  // AC-5: messages without showTipPopup===true are skipped.
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    if (mode !== 'serverless') return; // Mirror of MCP effect's serverless skip

    // Initial load: record existing struggled completions so we don't pop historic tips.
    // Mirrors isInitialLoadRef / isInitialLoadRef.current guard in the MCP effect (L353–361).
    if (isInitialTipLoadRef.current) {
      isInitialTipLoadRef.current = false;
      for (const msg of messages) {
        if (
          msg.decisionType === 'question_complete' &&
          msg.showTipPopup &&
          msg.questionNumber != null
        ) {
          shownTipForQuestionRef.current.add(msg.questionNumber);
        }
      }
      return;
    }

    for (const msg of messages) {
      if (msg.decisionType !== 'question_complete') continue;
      if (!msg.showTipPopup) continue; // AC-5: no popup when showTipPopup is false/absent
      if (!msg.learningTip) continue; // defensive: no text → no empty popup
      const qn = msg.questionNumber;
      if (qn == null) continue; // can't dedup without a key — skip
      if (shownTipForQuestionRef.current.has(qn)) continue; // AC-4 dedup — already shown
      // Mark BEFORE enqueue to block re-entry on next poll (AC-4).
      shownTipForQuestionRef.current.add(qn);

      // US-004 / AC-1 / DD-005 / TO-LL-037: Enqueue tip AHEAD of any celebration.
      // If a struggled celebration is stashed (pendingCelebrationRef), flush it
      // IMMEDIATELY AFTER the tip so the pair renders tip → celebration in one
      // deterministic batch — eliminates the cross-source ordering race (§2 of US-004 guide).
      // The fallback timer is cancelled here because the tip arrived before it fired.
      const held = pendingCelebrationRef.current;
      pendingCelebrationRef.current = null;
      if (pendingCelebrationTimerRef.current) {
        clearTimeout(pendingCelebrationTimerRef.current);
        pendingCelebrationTimerRef.current = null;
      }
      setPopupQueue((prev) => [
        { kind: 'tip', tipText: msg.learningTip! },
        ...(held ? [{ kind: 'celebration' as const, award: held }] : []),
        ...prev,
      ]);
    }
  }, [messages, mode]);

  // Show next popup from queue when no popup is currently displayed
  useEffect(() => {
    if (currentPopup === null && popupQueue.length > 0) {
      // Take the first item from the queue
      const [nextPopup, ...remainingQueue] = popupQueue;
      setCurrentPopup(nextPopup);
      setPopupQueue(remainingQueue);
    }
  }, [currentPopup, popupQueue]);

  // Handle popup dismissal (auto-timer or user tap)
  const handlePopupDismiss = useCallback(() => {
    setCurrentPopup(null);
    // The useEffect above will automatically show the next queued popup
  }, []);

  // US-004: Cleanup the 1500ms fallback timer on unmount to prevent a stale
  // state update after the component is gone (no timer leak).
  useEffect(
    () => () => {
      if (pendingCelebrationTimerRef.current) clearTimeout(pendingCelebrationTimerRef.current);
    },
    []
  );

  // US-005: Auto-complete session when free user reaches question limit
  useEffect(() => {
    if (isQuestionLimitReached && status === 'active' && !isCompleting) {
      completeSession();
    }
  }, [isQuestionLimitReached, status, isCompleting, completeSession]);

  // BUG-20260209-105903: Callback for profile upload success - invalidates cache to refresh icon - 2026-02-09
  const handleUploadSuccess = useCallback(() => {
    // Invalidate the user profile query to trigger immediate refresh.
    // Only ever reachable when the viewer IS the student — the upload control is suppressed
    // while a parent is driving, precisely so this cannot rewrite her own avatar from a
    // screen showing her child's name.
    queryClient.invalidateQueries({ queryKey: ['user', 'profile'] });
  }, [queryClient]);

  // Handle loading state
  if (isLoading) {
    return <LoadingState message="Loading session..." />;
  }

  // Handle error state
  if (error) {
    const isNotFound = error.message?.includes('404') || error.message?.includes('not found');

    return (
      <ErrorState
        title={isNotFound ? 'Session Not Found' : 'Failed to Load Session'}
        message={
          isNotFound
            ? 'This session could not be found. It may have been deleted.'
            : error.message || 'Unable to load session. Please check your connection and try again.'
        }
        onRetry={() => navigate('/sessions')}
      />
    );
  }

  // US-002: gate the answer input while a learning-tip popup is open (AC-1/AC-3).
  // Derived synchronously from currentPopup so a background question load cannot
  // re-enable the input; only handlePopupDismiss clears it (AC-2).
  const isTipPopupOpen = currentPopup?.kind === 'tip';

  return (
    <div
      className="min-h-screen landscape:h-screen lg:h-screen flex flex-col bg-otter-paper"
      style={{ minHeight: 'calc(var(--vh, 1vh) * 100)' }}
    >
      {/* Header — warm chip header (matches the dashboards/sessions redesign) */}
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-otter-sand bg-otter-paper/80 px-3 py-2 backdrop-blur-md md:px-6 md:py-3">
        <div className="flex items-center gap-2">
          {/* first-run-guide (T9): a parent driving her child's session must go back to HER
              dashboard, not to the child's session list, which is not hers and which she has
              no reason to be looking at. */}
          <Link
            to={isDrivingForChild ? ROUTES.PARENT_DASHBOARD : '/sessions'}
            className="inline-flex items-center gap-0.5 rounded-lg px-2 py-1.5 text-sm font-bold text-pebbles-500 transition-colors hover:bg-otter-sand/40 hover:text-otter-earth-800"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back</span>
          </Link>
          {/* BUG-20260701-230408 (RC-002): pass the active sessionId so clicking the logo
              mid-session resumes this session instead of dropping context - 2026-07-02 */}
          <AppLogo resumeSessionId={sessionId} />
        </div>

        {/* RIGHT: pebbles chip + profile chip + status/actions */}
        {/* BUG-20260209-105903: Wire onUploadSuccess callback to enable immediate visual feedback - 2026-02-09 */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Pebbles chip */}
          <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 shadow-sm">
            <PebblesDisplay totalPebbles={displayPebbles} size="sm" />
          </span>
          {/* Identity chip */}
          <span className="inline-flex items-center rounded-full border border-otter-sand bg-white px-2 py-1 shadow-sm">
            <StudentProfileHeader
              displayName={displayName}
              profileImageUrl={displayAvatarUrl}
              {...(isDrivingForChild ? {} : { onUploadSuccess: handleUploadSuccess })}
            />
          </span>

          {/* Connection status indicator (only for MCP sessions) */}
          {!isPolling && !isSessionCompleted && mode !== 'serverless' && (
            <span className="hidden text-xs font-semibold text-otter-orange-500 sm:inline">
              Paused - switch back to resume
            </span>
          )}

          {/* US-016 AC-1/AC-2: "Report a problem" affordance — gated by isSystemError.
              Shown ONLY when a genuine system error is present (never for demo/validation errors).
              Styled subtly (muted text, small) to distinguish from the primary End Session button.
              TO-LL-013: navigate is handled inside useCancelSession.onSuccess — NOT here. */}
          {showReportIssue && (
            <button
              type="button"
              onClick={() => {
                // Reset any prior cancel error so the dialog opens cleanly
                resetCancel();
                setIsReportDialogOpen(true);
              }}
              className="text-xs text-gray-400 underline underline-offset-2 transition-colors hover:text-gray-600"
              aria-label="Report a problem with this session"
            >
              Report a problem
            </button>
          )}

          {/* TS-007 (T6, FR-B1 / AC-TS007.2): the student self-serve End Session
              control has been REMOVED for English sessions — only a parent can end
              a live session now, from the parent dashboard's EndSessionConfirmDialog
              (T7/T8/T9). MathCoachPage is untouched and keeps its own End Session
              control (auto-complete via useCompleteSession still fires above on
              question-limit for demo users; that logic is unaffected by this removal). */}

          {/* TS-007 (T6, AC-TS007.1): distinct 'ended by your parent' terminal
              message — rendered INSTEAD of the generic Completed badge, since an
              ended session has no report/star/summary to imply. */}
          {isEndedByParent && (
            <span
              role="status"
              className="max-w-[200px] text-right text-xs font-semibold leading-tight text-otter-earth-700 sm:max-w-none sm:text-sm"
            >
              Your parent ended this session. You can start a new one whenever you are ready.
            </span>
          )}

          {/* Completed badge — unchanged for genuine completion/cancellation */}
          {isSessionCompleted && !isEndedByParent && (
            <span className="flex items-center gap-1 text-sm font-semibold text-green-600">
              <CheckCircle className="h-4 w-4" />
              Completed
            </span>
          )}
        </div>
      </header>

      {/* Combined header rail — single warm strip below the header carrying the
          passage heading (left) and, for serverless sessions, the question
          progress indicator with its "Attempt X/Y" badge (right).
          - Passage heading (US-017 AC-1/AC-3): resume affordance so a returning
            student immediately recognises the passage. Uses truncateText (same
            utility as SessionCard) — React escapes the text, so no HTML-injection
            risk (TO-LL-013 pattern).
          - Question progress rail: serverless sessions only.
          Renders whenever either piece is available; hidden when the session is
          loading, in error state, or completed. */}
      {!isSessionCompleted &&
        (resumePassageHeading || (mode === 'serverless' && questionProgress)) && (
          <div className="flex items-center justify-between gap-3 border-b border-otter-sand bg-otter-cream/40 px-3 py-2 md:px-6">
            {resumePassageHeading ? (
              <p className="min-w-0 flex-1 truncate text-sm font-medium text-otter-earth-700 md:text-base">
                {resumePassageHeading}
              </p>
            ) : (
              <span className="flex-1" />
            )}
            {mode === 'serverless' && questionProgress && (
              <div className="shrink-0">
                <QuestionProgressIndicator questionProgress={questionProgress} />
              </div>
            )}
          </div>
        )}

      {/*
        first-run-guide (T9): the honesty line.

        WHY IT EXISTS: nothing in this design stops a parent typing her child's answers, and
        nothing can — the removed password handover would not have stopped it either. So rather
        than pretend the problem is solved, the screen states plainly whose work is being
        recorded. Without it, a parent "helping" produces a report describing her child's
        reading built from her own answers, and the first she learns of it is the report itself.

        Deliberately quiet: no popup, nothing to dismiss, blocks nobody. Alice's framing, which
        is the right one: it should read as the reason the report can be trusted, not as a
        caution aimed at the parent.

        Only shown when the viewer is NOT the student, so a child sees nothing new.
      */}
      {isDrivingForChild && !isSessionCompleted && (
        <div
          role="note"
          className="flex items-center gap-2 border-b border-otter-sand bg-otter-cream/60 px-3 py-2 text-xs text-otter-earth-700 md:px-6 md:text-sm"
        >
          <span aria-hidden="true">👧</span>
          <span>
            This is <strong className="font-bold">{displayName}</strong>&apos;s practice.
            Everything answered here counts as their work and goes into their report.
          </span>
        </div>
      )}

      {/* Main content area - responsive split panel */}
      <div className="flex-1 overflow-hidden flex flex-col landscape:flex-row lg:flex-row w-full transition-all duration-300 ease-in-out">
        {/* Left panel: Passage (visible only on wide viewports) */}
        <PassagePanel passage={passage ?? ''} highlightSessionId={sessionId} />

        {/* Right panel: Conversation (full width on narrow, 60% on wide) */}
        {/* BUG-20260209-105903: Pass profileImageUrl to enable custom avatars in chat - 2026-02-09 */}
        <ConversationPanel
          cannotAnswerReason={
            viewerMayAnswer
              ? null
              : `This is ${displayName || 'your child'}'s practice and only they can answer in it. You can read along here.`
          }
          messages={displayMessages}
          messageContainerRef={messageContainerRef}
          passage={passage}
          passageSessionId={sessionId}
          isSessionCompleted={isSessionCompleted}
          isTimedOut={isTimedOut}
          onSubmitAnswer={submitAnswer}
          isSubmitting={isSubmitting}
          submitError={submitError?.message || null}
          isPolling={isPolling}
          profileImageUrl={displayAvatarUrl}
          totalQuestions={totalQuestions}
          isDemoLimitReached={
            !isSessionCompleted && (isAttemptLimitReached || isQuestionLimitReached)
          }
          isTipPopupOpen={isTipPopupOpen}
          // GAP-C (C-9): one hop, parent to child. Resolved live-then-persisted above,
          // so it survives a reload. Absent reads as scaffold inside the panel.
          questionPhase={questionProgress?.phase}
          // BUG-20260731-163953 (RC-004/RC-005, T-18): one hop, mirroring the
          // questionPhase prop wiring directly above.
          segmentationDegraded={segmentationDegraded}
          completionSummary={
            // TS-007 (T6): no report/summary exists for an ended_by_parent session —
            // the distinct terminal message (header, above) covers that case instead.
            isSessionCompleted && !isEndedByParent ? (
              <SessionCompletionSummary
                totalPebblesEarned={completionMetrics.totalPebblesEarned}
                firstTryStreak={completionMetrics.firstTryStreak}
                totalQuestions={completionMetrics.totalQuestions}
                improvementTip={completionMetrics.improvementTip}
                sessionSummary={effectiveSessionSummary}
                starRating={starRating}
                // first-run-guide (T11): a parent who drove this session must NOT be sent to
                // the student session list, where "Start a new session" would create a session
                // with HER as the student — the removed self-trial through a different door.
                isDrivingForChild={isDrivingForChild}
                childName={displayName}
                onBackToParentDashboard={() => navigate(ROUTES.PARENT_DASHBOARD)}
              />
            ) : undefined
          }
        />
      </div>

      {/* US-001: Learning tip popup — renders when currentPopup is a tip item (DD-005).
          Button is the sole exit (AC-3); countdown disables it until 0 (AC-2). */}
      <LearningTipPopup
        isVisible={currentPopup?.kind === 'tip'}
        tipText={currentPopup?.kind === 'tip' ? currentPopup.tipText : ''}
        countdownSeconds={countdownSeconds}
        onDismiss={handlePopupDismiss}
      />

      {/* Celebration popup overlay - renders on top of everything when kind==='celebration' */}
      <CelebrationPopup
        totalAwarded={currentPopup?.kind === 'celebration' ? currentPopup.award.totalAwarded : 0}
        bonusAwarded={currentPopup?.kind === 'celebration' ? currentPopup.award.bonusAwarded : 0}
        onDismiss={handlePopupDismiss}
        isVisible={currentPopup?.kind === 'celebration'}
      />

      {/* US-005/US-006: Demo limit popup overlay - renders above celebration popup */}
      {effectiveDemoPopupReason && (
        <DemoLimitPopup
          isVisible={effectiveDemoPopupReason !== null}
          reason={effectiveDemoPopupReason}
          onDismiss={handleDemoLimitDismiss}
          maxAttemptsPerQuestion={maxAttemptsPerQuestion ?? undefined}
          maxQuestionsPerSession={maxQuestionsPerSession ?? undefined}
          maxSessions={maxSessions ?? undefined}
          pebblesAwarded={attemptLimitPebbles ?? undefined}
        />
      )}

      {/* US-016: Report Issue confirmation dialog — renders above all other overlays.
          Navigation to /sessions on success is handled by useCancelSession.onSuccess (TO-LL-013).
          Dismiss keeps the student on the chat with no side-effects (AC-5 dismiss branch).
          CCM-03: no coin numbers shown — only the AC-4 reassurance string inside the dialog. */}
      <ReportIssueDialog
        isVisible={isReportDialogOpen}
        isSubmitting={isCancelling}
        errorMessage={cancelError ? 'Could not end the session. Please try again.' : null}
        onConfirm={() => cancelSession('report_issue')}
        onDismiss={() => setIsReportDialogOpen(false)}
      />
    </div>
  );
}
